import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import store from 'store';

// console.log(' 🚀 reverbEcho module loaded');

let echoInstance;

const getAuthHeaders = () => {
  try {
    const state = store.getState();
    const token = state?.auth?.token;
    return token
      ? {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      }
      : {};
  } catch {
    return {};
  }
};

const stripTrailingSlash = (url) => String(url || '').replace(/\/+$/, '');
const normalizeHost = (value = '') => String(value).replace(/^https?:\/\//, '').replace(/\/+$/, '');

const deriveLaravelOrigin = () => {
  const explicit = process.env.REACT_APP_LARAVEL_ORIGIN;
  if (explicit) return stripTrailingSlash(explicit);

  const apiBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_PATH;
  if (!apiBase) return '';

  const cleaned = stripTrailingSlash(apiBase);
  return cleaned.replace(/\/api$/, '');
};

const createEcho = () => {
  if (typeof window !== 'undefined') {
    window.Pusher = Pusher;
  }

  const key =
    process.env.REACT_APP_REVERB_APP_KEY ||
    process.env.REACT_APP_PUSHER_KEY ||
    'dummy-key';

  const host = normalizeHost(process.env.REACT_APP_REVERB_HOST);
  const scheme = process.env.REACT_APP_REVERB_SCHEME || 'https';
  const forceTLS = scheme === 'https';
  const port = Number(process.env.REACT_APP_REVERB_PORT || 443);
  const cluster =
    process.env.REACT_APP_REVERB_APP_CLUSTER ||
    process.env.REACT_APP_PUSHER_CLUSTER ||
    'mt1';

  const laravelOrigin = deriveLaravelOrigin();
  const authEndpoint = laravelOrigin
    ? `${laravelOrigin}/broadcasting/auth`
    : '/broadcasting/auth';

  // Pusher-js ALWAYS requires `cluster` in options (throws if missing).
  // However, when `wsHost` is explicitly set, Pusher-js uses wsHost for
  // routing and ignores the cluster-based host — so this is safe.
  const echoConfig = {
    broadcaster: 'pusher',
    key,
    cluster,          // required by Pusher-js validation
    forceTLS,
    encrypted: forceTLS,
    disableStats: true,
    // ✅ Pusher-js only supports 'ws' and 'sockjs' as transport names.
    //    'wss' is NOT valid — wss:// is used automatically when forceTLS=true.
    enabledTransports: ['ws'],

    // wsHost takes routing priority over cluster when explicitly provided
    ...(host
      ? {
        wsHost: host,
        wsPort: port,
        wssPort: port,
      }
      : {}),

    authEndpoint,
    auth: {
      headers: getAuthHeaders(),
    },
  };


  const instance = new Echo(echoConfig);

  // ── Pusher connection state logging ──────────────────────────────────
  const pusher = instance?.connector?.pusher;
  if (pusher?.connection) {
    pusher.connection.bind('state_change', ({ previous, current }) => {
      const emoji = {
        connecting: '🔄',
        connected: '✅',
        disconnected: '🔴',
        failed: '❌',
        unavailable: '⚠️',
        initialized: '🔵',
      }[current] || '❓';
      console.log(` ${emoji} Connection state: ${previous} → ${current}`);
    });

    pusher.connection.bind('connected', () => {
      // console.log(' ✅ WebSocket CONNECTED — socket_id:', pusher.connection.socket_id);
      console.log('✅ [Socket CONNECTED]');
    });

    pusher.connection.bind('error', (err) => {
      console.error(' ❌ Connection ERROR:', err);
    });

    pusher.connection.bind('failed', () => {
      console.error(' ❌ Connection FAILED — browser may not support WebSockets');
    });

    pusher.connection.bind('disconnected', () => {
      console.warn(' 🔴 Connection DISCONNECTED');
    });

    pusher.connection.bind('unavailable', () => {
      console.warn(' ⚠️ Connection UNAVAILABLE — will retry...');
    });

    // Log whatever state we are already in (Echo's internal connect()
    // fires during construction — before we can bind — so we may have
    // already passed 'initialized → connecting' by now).
    const currentState = pusher.connection.state;
    console.log(' 🔵 State at listener-bind time:', currentState);

    if (currentState === 'connected') {
      console.log(' ✅ Already connected — socket_id:', pusher.connection.socket_id);
    }

    // If for any reason the connection hasn't started yet, force it now
    // that our listeners are attached.
    if (currentState === 'initialized' || currentState === 'disconnected') {
      console.log(' 🔧 Explicitly calling pusher.connect()...');
      pusher.connect();
    }
  } else {
    console.warn(' ⚠️ Could not attach connection listeners — pusher.connection not available');
  }
  // ─────────────────────────────────────────────────────────────────────

  return instance;
};

export const getEcho = () => {

  if (!echoInstance) {
    // console.log(' 🔧 Creating Echo instance lazily via getEcho()');
    echoInstance = createEcho();
  } else {
    // console.log(' ♻️ Reusing existing Echo instance');
  }
  return echoInstance;
};

const listenSafe = (channel, eventName, handler) => {
  if (typeof handler !== 'function') return;
  channel.listen(eventName, handler);
  if (eventName && !String(eventName).startsWith('.')) {
    channel.listen(`.${eventName}`, handler);
  }
};

export const subscribeUserCampaign = (userId, handlers = {}) => {
  const echo = getEcho();
  const channelName = `campaign.${userId}`;

  console.log(` 📡 Subscribing to private channel: "${channelName}" at ${new Date().toLocaleTimeString()}`);

  const channel = echo.private(channelName);

  // Pusher subscription state callbacks
  channel
    .subscribed(() => {
      console.log(` ✅ Successfully subscribed to channel: "${channelName}" at ${new Date().toLocaleTimeString()}`);
    })
    .error((err) => {
      console.error(` ❌ Subscription error on channel "${channelName}":`, err);
    });

  const events = ['.campaign.progress', '.campaign.completed', '.campaign.failed'];
  events.forEach((evt) => console.log(` 👂 Listening for event: "${evt}" on "${channelName}" at ${new Date().toLocaleTimeString()}`));

  listenSafe(channel, '.campaign.progress', handlers.onProgress);
  listenSafe(channel, '.campaign.completed', handlers.onCompleted);
  listenSafe(channel, '.campaign.failed', handlers.onFailed);

  return channel;
};

export const leaveUserCampaign = (userId) => {
  if (!echoInstance) return;
  try {
    console.log(` 🚪 Leaving channel: "campaign.${userId}"`);
    echoInstance.leave(`campaign.${userId}`);
  } catch (err) {
    console.warn(` ⚠️ Error leaving channel "campaign.${userId}":`, err);
  }
};

export const subscribeUserChat = (userId, handlers = {}) => {
  const echo = getEcho();
  const channelName = `chat.${userId}`;

  console.log(` 📡 Subscribing to private channel: "${channelName}"`);

  const channel = echo.private(channelName);

  channel
    .subscribed(() => {
      console.log(` ✅ Subscribed to channel: "${channelName}"`);
    })
    .error((err) => {
      console.error(` ❌ Subscription error on channel "${channelName}":`, err);
    });

  listenSafe(channel, '.chat.menu.updated', handlers.onMenuUpdated);
  listenSafe(channel, '.chat.message.incoming', handlers.onIncomingMessage);
  listenSafe(channel, '.chat.message.meta', handlers.onMessageMeta);
  listenSafe(channel, '.chat.message.status', handlers.onMessageStatus);
  listenSafe(channel, '.chat.assignment.updated', handlers.onAssignmentUpdated);

  return channel;
};

export const leaveUserChat = (userId) => {
  if (!echoInstance) return;
  try {
    console.log(` 🚪 Leaving channel: "chat.${userId}"`);
    echoInstance.leave(`chat.${userId}`);
  } catch (err) {
    console.warn(` ⚠️ Error leaving channel "chat.${userId}":`, err);
  }
};

const reverbEcho = {
  getEcho,
  subscribeUserCampaign,
  leaveUserCampaign,
  subscribeUserChat,
  leaveUserChat,
};

export default reverbEcho;
